/**
 * Client actions returned by chat-concierge dashboard UI tools (phase 2g).
 * Executed on Home / drawer / in-modal Adjust — never on public landing chat.
 */

export type DashboardClientAction =
  | { type: "set_home_view"; view: "list" | "calendar" }
  | { type: "open_celebration"; celebration_id: string }
  | { type: "set_list_filter"; filter: string }
  | { type: "set_list_range"; range: string }
  | {
      type: "open_bulk_period";
      period_key: string;
      celebration_ids: string[];
    };

export interface DashboardClientActionHandlers {
  setHomeView: (view: "list" | "calendar") => void;
  openCelebration: (celebrationId: string) => void;
  setListFilter?: (filter: string) => void;
  setListRange?: (range: string) => void;
  openBulkPeriod?: (args: { period_key: string; celebration_ids: string[] }) => void;
}

export function parseDashboardClientActions(raw: unknown): DashboardClientAction[] {
  if (!Array.isArray(raw)) return [];
  const out: DashboardClientAction[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const type = row.type;
    if (type === "set_home_view" && (row.view === "list" || row.view === "calendar")) {
      out.push({ type: "set_home_view", view: row.view });
    } else if (type === "open_celebration" && typeof row.celebration_id === "string") {
      const id = row.celebration_id.trim();
      if (id) out.push({ type: "open_celebration", celebration_id: id });
    } else if (type === "set_list_filter" && typeof row.filter === "string") {
      const filter = row.filter.trim();
      if (filter) out.push({ type: "set_list_filter", filter });
    } else if (type === "set_list_range" && typeof row.range === "string") {
      const range = row.range.trim();
      if (range) out.push({ type: "set_list_range", range });
    } else if (type === "open_bulk_period" && typeof row.period_key === "string") {
      const period_key = row.period_key.trim();
      const celebration_ids = Array.isArray(row.celebration_ids)
        ? row.celebration_ids.filter(
            (id): id is string => typeof id === "string" && id.trim().length > 0,
          )
        : [];
      if (period_key && celebration_ids.length > 0) {
        out.push({ type: "open_bulk_period", period_key, celebration_ids });
      }
    }
  }
  return out;
}

export function applyDashboardClientActions(
  actions: DashboardClientAction[],
  handlers: DashboardClientActionHandlers,
): void {
  for (const action of actions) {
    switch (action.type) {
      case "set_home_view":
        handlers.setHomeView(action.view);
        break;
      case "open_celebration":
        handlers.openCelebration(action.celebration_id);
        break;
      case "set_list_filter":
        handlers.setListFilter?.(action.filter);
        break;
      case "set_list_range":
        handlers.setListRange?.(action.range);
        break;
      case "open_bulk_period":
        handlers.openBulkPeriod?.(action);
        break;
      default:
        break;
    }
  }
}
